import { useGetLmsTrainingContentOutputByTrainingIdQuery, useAddLmsTrainingContentOutputMutation, useUpdateLmsTrainingContentOutputMutation, useDeleteLmsTrainingContentOutputMutation } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { formatErrorMessage } from '../../../../utils/errorUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';

/**
 * Eğitim detayındaki "İçerik / Kazanım" sekmesi için veri hook'u.
 * Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 1.md §5.
 *
 * `GetByTrainingId` kayıt yoksa 404 döner — bu bir hata değil, "henüz içerik girilmemiş"
 * anlamına gelir; bu durumda boş bir 6 dilli form gösterilip kayıt `Add` ucuyla açılır.
 */
const useTrainingContentOutputData = (trainingId) => {
    const { th } = useLanguage();

    const { data, isLoading, isFetching, error, refetch } = useGetLmsTrainingContentOutputByTrainingIdQuery(trainingId, { skip: !trainingId, refetchOnMountOrArgChange: true });

    const notFound = error?.status === 404;
    const hasOtherError = !!error && !notFound;

    const [addMutation, { isLoading: isAdding }] = useAddLmsTrainingContentOutputMutation();
    const [updateMutation, { isLoading: isUpdating }] = useUpdateLmsTrainingContentOutputMutation();
    const [deleteMutation, { isLoading: isDeleting }] = useDeleteLmsTrainingContentOutputMutation();

    const saveContentOutput = async (translations) => {
        try {
            if (data && !notFound) {
                await updateMutation({ trainingId, translations }).unwrap();
            } else {
                await addMutation({ trainingId, translations }).unwrap();
            }
            toastService.success(th('trainingCatalog.contentOutput.saveSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            // 409: eğitimin içerik kaydı zaten var — Update ucuna geçilmeli (normalde buraya düşmemeli,
            // çünkü `data` mevcutsa zaten Update çağrılıyor; yine de beklenmedik bir yarış durumuna karşı ele alınır)
            toastService.error(formatErrorMessage(err) || th('trainingCatalog.contentOutput.saveError'), th('common.error'));
            return false;
        }
    };

    const deleteContentOutput = async () => {
        try {
            await deleteMutation(trainingId).unwrap();
            toastService.success(th('trainingCatalog.contentOutput.deleteSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('trainingCatalog.contentOutput.deleteError'), th('common.error'));
            return false;
        }
    };

    return {
        contentOutput: notFound ? null : data,
        isLoading: isLoading && !notFound,
        isFetching,
        hasOtherError,
        isAdding,
        isUpdating,
        isDeleting,
        saveContentOutput,
        deleteContentOutput,
        refetch
    };
};

export default useTrainingContentOutputData;
